// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { HistoryType } from "../../../Utils/History/HistoryType.ts";
import { ChartDataType } from "../../../Chart/Utils/ChartConstants.js";
import { ConstantVector } from "./ConstantVector.js";
import { DoubleMatrix, ExpandDoubleVector } from "./DoubleMatrix.js";
import { HashtableLightCount } from "./HashtableLightCount.js";
import { MarkersVector } from "./MarkersVector.js";
import { ResVector } from "./ResVector.js";
import { ErrorInformationStorage } from "../../ErrorInformationStorage.js";
import { Resources } from "../../properties/Resources.js";
import { Periods } from "../../../Utils/History/TFInfo.ts";
import { HistoricalData } from "./HistoricalData.js";
import { DynProperty, ColorStyleWidth } from "../../DynProperty.js";
import { IndicatorScriptBase } from "./IndicatorScriptBase.ts";


//Processor
export let Indicator = function ()
{
    this.FParent = null;
    this.Script = null;
    this.SeparateWindow = true;
    this.quickAccessData = null;
    this.FCount = 0;

    //TODO MB
    this.Digits = -1;
}

Indicator.prototype.FillFields = function ()
{
    if (this.Script === null)
        throw new Error("Used witout Script!!!");

    let linesCount = this.Script.LinesCount;

    this.LinesCount = linesCount;
    this.LinePlateVisible = new Array(linesCount);
    this.LineVisible = new Array(linesCount);
    this.LineStyles = new Array(linesCount);
    this.LineWidth = new Array(linesCount);
    this.LineColors = new Array(linesCount);
    this.PriceMultiplier = new Array(linesCount);
    this.TimeShift = new Array(linesCount);
    this.FLineNames = new Array(linesCount);
    this.EmptyValue = new Array(linesCount);
    this.DrawBegin = new Array(linesCount);
    this.LineMarkers = new Array(linesCount);
    // this.HistogrammStyles = new Array(linesCount);

    this.Script.Data = new DoubleMatrix(linesCount);
    //TODO подумать
    this.Script.LineMarkers = this.LineMarkers;

    this.FCloudBuffer = new HashtableLightCount();
    this.Script.FCloudBuffer = this.FCloudBuffer;

    for (let i = 0; i < linesCount; i++)
    {
        this.TimeShift[i] = 0;
        this.PriceMultiplier[i] = 1.0;
        this.Script.Data.set(i, new ResVector(i, this.TimeShift, this.PriceMultiplier, new ExpandDoubleVector()));
        //this.HistogrammStyles[i] = -1;
        this.LineVisible[i] = true;
        this.LinePlateVisible[i] = true;
        this.EmptyValue[i] = Number.MAX_VALUE;
        this.LineMarkers[i] = new MarkersVector();
        this.DrawBegin[i] = 0;
    }


    let values = this.Script.GetValues();

    let param = null;
    for (let i = 1; i <= linesCount; i++)
    {
        let lineIndex = i - 1;
        param = values["line" + i + ".Name"];
        if (param !== null && param !== undefined)
            this.FLineNames[lineIndex] = param;
        param = values["line" + i + ".Color"];
        if (param !== null && param !== undefined)
            this.LineColors[lineIndex] = param;
        param = values["line" + i + ".Style"];
        if (param !== null && param !== undefined)
            this.LineStyles[lineIndex] = param;
        param = values["line" + i + ".Width"];
        if (param != null)
            this.LineWidth[lineIndex] = param;
        param = values["line" + i + ".Symbol"];
        if (param !== null && param !== undefined)
            this.SymbolCode[lineIndex] = param;
        param = values["line" + i + ".Visible"];
        if (param !== null && param !== undefined)
            this.LineVisible[lineIndex] = param;
    }

    param = values.separateWindow;
    if (param !== null && param !== undefined)
        this.SeparateWindow = param

    //TODO
    this.Script.Init();

    this.InitIndicatorLevelLines();

    this.Script.UpdateIndicatorLevelLineValueHandler = this.UpdateIndicatorLevelLineValueHandler.bind(this);

    this.CacheQuickAccessData();

    //TODO MB
    for (let i = 0; i < linesCount; i++)
        this.DrawBegin[i] = this.Script.DrawBegin[i] || 0;
}
Indicator.prototype.Properties = function ()
{
    let properties = new Array();
    try
    {
        properties = properties.concat(this.MainProperties());
        properties = properties.concat(this.AdditionalProperties());
    }
    catch (e)
    {
        ErrorInformationStorage.GetException(e);
    }
    return properties;
};

Indicator.prototype.MainProperties = function ()
{
    let properties = new Array();

    if (this.Script === null)
        return properties;

    properties = properties.concat(this.Script.Properties());

    return properties;
};

Indicator.prototype.AdditionalProperties = function ()
{
    let additionalProperties = new Array();

    let curGroup = "";
    // Загоняем общие параметры
    for (let i = 0; i < this.LinesCount; i++)
    {
        let levelLine = this.getLineData(i);//as ConstantVector;
        let isLevelLine = levelLine.isConstantVector;//TODO levelLine != null;
        // #97150
        let lineNumber = this.LinesCount === 1 ? "" : (i + 1);

        curGroup = Resources.getResource("property.Line")
        curGroup += " #" + lineNumber
        curGroup += " " + Resources.getResource("property.lineProperties");

        let prop = new DynProperty("Visible " + i, this.LineVisible[i], DynProperty.BOOLEAN, curGroup);
        prop.propertyComment = Resources.getResource("property.line.Visible") + " " + lineNumber;
        prop.sortIndex = 1;
        additionalProperties.push(prop);

        prop = new DynProperty("Draw Plate " + i, this.LinePlateVisible[i], DynProperty.BOOLEAN, curGroup);
        prop.propertyComment = Resources.getResource("property.line.plateVisible").replace("{0}", lineNumber);
        prop.sortIndex = 8;
        additionalProperties.push(prop);

        prop = new DynProperty("Line " + i, new ColorStyleWidth(this.LineColors[i], this.LineStyles[i], this.LineWidth[i]) /*{ AllLineStyles = true }*/, DynProperty.COLOR_STYLE_WIDTH, curGroup) /*{ SortIndex = 2 }*/;
        prop.propertyComment = Resources.getResource("property.Line") + " " + lineNumber;
        prop.sortIndex = 2;
        prop.objectVariants = IndicatorScriptBase.GetLineTypesArray();
        prop.objectVariants.useStyleProperties = true;
        additionalProperties.push(prop);

        prop = new DynProperty("Line Name " + i, this.FLineNames[i], DynProperty.STRING, curGroup);
        prop.propertyComment = Resources.getResource("property.line.lineName") + " " + lineNumber;
        prop.sortIndex = 3;
        additionalProperties.push(prop);

        if (!isLevelLine)
        {
            //remove
            //prop = new DynProperty("Y factor " + i, this.PriceMultiplier[i], DynProperty.DOUBLE, curGroup);
            //prop.propertyComment = Resources.getResource("property.line.yFactor") + " " + lineNumber;
            //prop.sortIndex = 4;
            //additionalProperties.push(prop);
            // #97150
            if (!this.Script.HideLineTimeShift)
            {
                prop = new DynProperty("Time Shift " + i, this.TimeShift[i], DynProperty.INTEGER, curGroup);
                prop.propertyComment = Resources.getResource("property.line.timeShift") + " " + lineNumber;
                prop.maximalValue = Indicator.NUMERIC_MAX_VALUE;
                prop.minimalValue = -Indicator.NUMERIC_MAX_VALUE;
                prop.sortIndex = 6;
                additionalProperties.push(prop);
            }
        }
        else
        {
            prop = new DynProperty("Value " + i, levelLine.Value, DynProperty.DOUBLE, curGroup);
            prop.propertyComment = Resources.getResource("property.line.value") + " " + lineNumber;
            prop.maximalValue = Indicator.NUMERIC_MAX_VALUE;
            prop.minimalValue = -Indicator.NUMERIC_MAX_VALUE;
            prop.increment = 0.00001;
            prop.decimalPlaces = 5;
            prop.sortIndex = 4;
            additionalProperties.push(prop);
        }
    }

    //TODO не надо на вебе пока 
    //var aggProp = new DynProperty("ChartObjectTFList", TFConfig, DynProperty.CHATROBJECT_TIF_CONFIG, DynProperty.CHARTOBJECT_TIF_GROUP);
    //additionalProperties.push(aggProp);
    //TODO не надо на вебе пока 
    //помечаем настройки линий, видимости и прочей чепухи из индикатора  как те которые нужно скрыть в TSL
    //for (let dp in additionalProperties)
    //{
    //    array = dp.objectVariants;
    //    if (dp.objectVariants == null || dp.objectVariants.length == 0)
    //        array = new object[DynpropertyHiddenMode.Hidden];
    //    else //что-то есть, нужно просто добавить в массив
    //    {
    //        //TODO WTF???????
    //        //Array.Resize<object>(ref array, array.length + 1);
    //        array[array.length - 1] = DynpropertyHiddenMode.Hidden;
    //    }
    //    dp.objectVariants = array;
    //}

    return additionalProperties;

}

Indicator.prototype.callBack = function (props)
{
    let NeedRecalculate = false;
    if (this.Script !== null)
        NeedRecalculate |= this.Script.callBack(props);

    for (let i = 0; i < this.LinesCount; i++)
    {
        let levelLine = this.getLineData(i);//as ConstantVector;
        let isLevelLine = levelLine.isConstantVector;//TODO levelLine != null;
        let lineNumber = i + 1;


        let propName = "Visible " + i;
        let dp = DynProperty.getPropertyByName(props, propName);
        if (dp)
            this.LineVisible[i] = dp.value;


        propName = "Draw Plate " + i;
        dp = DynProperty.getPropertyByName(props, propName);
        if (dp)
            this.LinePlateVisible[i] = dp.value;


        propName = "Line " + i;
        dp = DynProperty.getPropertyByName(props, propName);
        if (dp)
        {
            this.LineColors[i] = dp.value.Color;
            this.LineStyles[i] = dp.value.Style;
            this.LineWidth[i] = dp.value.Width;
        }

        propName = "Line Name " + i;
        dp = DynProperty.getPropertyByName(props, propName);
        if (dp)
            this.FLineNames[i] = dp.value;

        if (!isLevelLine)
        {
            //remove
            //propName = "Y factor " + i;
            //dp = DynProperty.getPropertyByName(props, propName);
            //if (dp)
            //    this.PriceMultiplier[i] = dp.value;
            propName = "Time Shift " + i;
            dp = DynProperty.getPropertyByName(props, propName);
            if (dp)
                this.TimeShift[i] = dp.value;
        }
        else
        {
            propName = "Value " + i;
            dp = DynProperty.getPropertyByName(props, propName);
            if (dp)
                levelLine.Value = dp.value;
        }
    }

    //TODO MB
    for (let i = 0; i < this.LinesCount; i++)
        this.DrawBegin[i] = this.Script.DrawBegin[i] || 0;


    if (this.Script !== null)
        this.Script.ParametersRecalculationCallBack();

    if (NeedRecalculate)
        this.Refresh();
}

// #97150
Indicator.prototype.ShowOnTop = function ()
{
    return !this.Script.HideOnTop
}

Indicator.prototype.InitIndicatorLevelLines = function ()
{
    let len = this.Script.LevelLines.length;
    for (let i = 0; i < len; i++)
    {
        let lvl = this.Script.LevelLines[i];

        this.SetLevelValue(lvl.LineNumber, lvl.Level);
        this.SetLevelStyle(lvl.LineNumber, lvl.style, lvl.width, lvl.color);
        this.FLineNames[lvl.LineNumber] = lvl.Name;

        lvl.Vector = this.Script.Data.get(lvl.LineNumber);
        this.LinePlateVisible[lvl.LineNumber] = true;
        this.LineVisible[lvl.LineNumber] = true;
    }
}

Indicator.prototype.UpdateIndicatorLevelLineValueHandler = function (rowNumber, value)
{
    this.SetLevelValue(rowNumber, value);
}

Indicator.prototype.SetLevelValue = function (rowNumber, value)
{
    for (let i = 0; i < this.Script.Data.Length; i++)
    {
        let vector = this.Script.Data.get(i);
        if (vector !== null && vector.RowNumber === rowNumber && vector.isConstantVector)
        {
            vector.Value = value;
            return;
        }
    }

    this.LinesCount += 1;
    this.TimeShift.push(0)
    let cv = new ConstantVector(value, rowNumber, this.TimeShift, this.PriceMultiplier);
    let vector = this.Script.Data.get(0);
    for (let i = 0; i < vector.Length; i++)
        cv.Vector.Add(0.0);

    //TODO MB 
    this.EmptyValue[this.LinesCount - 1] = Number.MAX_VALUE;
    this.Script.Data.Length++;
    //
    this.Script.Data.set(this.LinesCount - 1, cv);
}

Indicator.prototype.SetLevelStyle = function (index, draw_style, line_width, clr)
{
    this.LineStyles[index] = draw_style;
    this.LineWidth[index] = line_width;
    this.LineColors[index] = clr;
}

Indicator.prototype.CacheQuickAccessData = function ()
{
    this.quickAccessData = null;
    this.quickAccessData = new ExpandDoubleVector(this.Script.Data.Length);
    for (let i = 0; i < this.Script.Data.Length; i++)
        this.quickAccessData[i] = this.Script.Data.get(i).Vector;
}

Indicator.prototype.getLineData = function (lineNumber)
{
    if (this.Script === null)
        throw new Error("Script Error");
    if (this.Script.Data)
        return this.Script.Data.get(lineNumber);
    else
        return null;
}

Object.defineProperty(Indicator.prototype, "CloudBuffer",
    {
        get: function () { return this.FCloudBuffer; }
    });

// TODO. Indicator cleanup after instrument change.
Indicator.prototype.Refresh = function ()
{
    if (!this.Script || !this.FParent)
        return

    let self = this
    // TODO. UGLY. Refactor.
    this.Script.RefreshPromise()
        .then(function ()
        {
            // Clear Data
            let dataLength = self.quickAccessData.Length
            for (let i = 0; i < dataLength; i++)
                self.quickAccessData[i].Clear()

            // +++
            for (let i = 0; i < self.LineMarkers.length; i++)
                self.LineMarkers[i].Clear();

            let len = self.FParent.FNonEmptyCashArrayCount
            for (let i = 0; i < len; i++)
            {
                self.NextBar()
                self.Script.OnQuote()
            }
        }).then(function ()
        {
            return this.Script.AfterRefreshPromise()
        }.bind(this))
}

Indicator.prototype.OnQuote = function ()
{
    if (this.Script !== null)
        this.Script.OnQuote();
}

Indicator.prototype.NextBar = function ()
{
    if (this.Script !== null)
    {
        this._NextBar();
        this.Script.NextBar();
    }
}

Indicator.prototype._NextBar = function ()
{
    let dataLength = this.quickAccessData.Length;
    for (let i = 0; i < dataLength; i++)
        this.quickAccessData[i].Add(this.EmptyValue[i]);

    if (this.FCloudBuffer.HasValues)
        for (let i = 0; i < this.FCloudBuffer.Count; i++)
            this.FCloudBuffer.Get(i).Add(0.0);
    // +++
    for (let i = 0; i < this.LineMarkers.length; i++)
        this.LineMarkers[i].Add();

    this.FCount = this.quickAccessData[0].Length;
}

Indicator.prototype.ShortName = function ()
{
    if (this.Script !== null)
        return this.Script.ShortName;

    return "";
}

Indicator.prototype.Dispose = function ()
{
    if (this.Script !== null)
        this.Script.Dispose();
}



Object.defineProperty(Indicator.prototype, "Parent",
    {
        get: function () { return this.FParent; },
        set: function (value)
        {
            this.FParent = value;
            this.Script.setHistoricalData(new HistoricalData(this))
        }
    });

//GetCashItemCount -> FCount

Indicator.prototype._GetPrice = function (priceType, offset)
{
    let count = this.FCount;
    return this.FParent.GetByType(count - 1 - offset, priceType);
};

Indicator.prototype._GetLength = function ()
{
    return this.FCount;
};

// #region From PTLIndicator.cs

export let PTLIndicator = function () { }//PTLIndicator || function () { }

PTLIndicator.GetStartIndex = function (parent, chart)
{
    if (!chart || !parent /*|| !(parent is CashItemFile)*/)
        return 0

    return Math.max(chart.DataExtenderAmount - 100, 0)
}

// #endregion From PTLIndicator.cs

// Period of data.
export let Period =
{
    // Tick Period.
    Tick: Periods.TIC,
    // 1 Minute Period.
    Min: Periods.MIN,
    // 5 Minute Period.
    Min5: Periods.MIN5,
    // 15 Minute Period.
    Min15: Periods.MIN15,
    // 30 Minute Period.
    Min30: Periods.MIN30,
    // 1 Hour Period.
    Hour: Periods.HOUR,
    // 4 Hour Period.
    Hour4: Periods.HOUR4,
    // 1 Day Period.
    Day: Periods.DAY,
    // 1 Week Pertiod.
    Week: Periods.WEEK,
    // 1 Monthl Period.
    Month: Periods.MONTH,
    // 1 Year Period.
    Year: Periods.YEAR,
    // 1 Second Period.
    Second: Periods.SECOND,
    // Price range period
    Range: Periods.RANGE
}

// Provide access to different history types.
Indicator.HistoryType =
{
    // Simple bars objects represent the Time, Tick, Price Range bars of data.
    Simple: ChartDataType.Default,
    // Represent access to Renko bars objects.
    Renko: ChartDataType.RencoNew,
    // Represent access to Kagi bars objects.
    Kagi: ChartDataType.Kagi,
    // Represent access to Line Break bars objects.
    LineBreak: ChartDataType.ThreeLinesBreak,
    // Represent access to PnF bars objects.
    PnF: ChartDataType.TicTac
}

// Style of bars aggregations.
// ByWicks and Advanced use only for Renko chart,
// ByHighLow use for Renko and PnF charts.
Indicator.BarsAggregations =
{
    // This value mean no, or classic aggregations type.
    None: 0,
    // This value will result in creating a renko data with wicks,
    // which will provide more information to the trader than the standard renko bricks do.
    ByWicks: 1,
    // Use high/low method, for Renko and PnF.
    ByHighLow: 2,
    // Use Renko Advanced mode
    Advanced: 4,
    // Use Renko Advanced high/low mode
    AdvancedHighLow: 8
}

// Supported history data types.
Indicator.DataType =
{
    // Quote history with Bid as source.
    Bid: HistoryType.QUOTE_LEVEL1,
    // Quote history with Ask as source.
    Ask: HistoryType.QUOTE_ASK,
    // History is building by trades.
    Trade: HistoryType.QUOTE_TRADES,
    // Quote history building by average between Asks and Bids.
    Average: HistoryType.QUOTE_BIDASK_AVG,
    // Quote history building by summa of max and min from Asks and Bids.
    Sum: HistoryType.QUOTE_BIDASK_SUM,
}

Indicator.NUMERIC_MAX_VALUE = 999999999;